import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import PropTypes from 'prop-types';
import { TextField, Select } from '@picsio/ui';

import l10n from '../../../../shared/strings';
import { MONEY_CURRENCY_CODES } from '../../helpers';
import { useBlurCommit, useEnterToBlur, useLocalFieldValue } from '../../hooks';
import {
  normalizeMoneyValue,
  serializeMoneyValue,
} from '../utils';
import ProductFieldRow from './ProductFieldRow';
import ProductFieldLabel from './ProductFieldLabel';

const MoneyField = ({
  data, value, disabled, isDiff, onChange, validate, savedError, onValidationChange,
}) => {
  const defaultCurrency = data.currency || 'USD';
  const normalized = useMemo(
    () => normalizeMoneyValue(value, defaultCurrency),
    [defaultCurrency, value],
  );
  const { localValue, setLocalValue } = useLocalFieldValue(normalized);
  const [currencyError, setCurrencyError] = useState(null);

  useEffect(() => {
    setLocalValue(normalizeMoneyValue(value, defaultCurrency));
  }, [defaultCurrency, setLocalValue, value]);

  const commit = useCallback((nextValue, onCancel) => {
    onChange(serializeMoneyValue(nextValue), data, onCancel);
  }, [data, onChange]);

  const { handleBlur: handleAmountBlur, error: amountError, clearErrorIfValid } = useBlurCommit({
    value: normalized,
    localValue,
    setLocalValue,
    onCommit: commit,
    serialize: serializeMoneyValue,
    validate,
    savedError,
  });

  const displayError = amountError || currencyError;

  useEffect(() => {
    if (onValidationChange) onValidationChange(displayError);
  }, [displayError, onValidationChange]);

  const handleCurrencyChange = useCallback((event) => {
    const nextValue = {
      ...localValue,
      currency: event.target.value,
    };
    setLocalValue(nextValue);
    if (currencyError) setCurrencyError(null);
    clearErrorIfValid(nextValue);
    commit(nextValue, () => setLocalValue(normalized));
  }, [clearErrorIfValid, commit, currencyError, localValue, normalized, setLocalValue]);

  const handleAmountChange = useCallback((event) => {
    const nextValue = { ...localValue, value: event.target.value };
    setLocalValue(nextValue);
    clearErrorIfValid(nextValue);
  }, [clearErrorIfValid, localValue, setLocalValue]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ProductFieldRow title={data.title}>
      <TextField
        value={localValue.value}
        label={<ProductFieldLabel title={data.title} tooltip={l10n.PRODUCT_INFO.fieldHelpers.money} isDiff={isDiff} />}
        disabled={disabled}
        type="number"
        error={Boolean(displayError)}
        helperText={displayError}
        InputProps={{ step: 'any' }}
        onChange={handleAmountChange}
        onBlur={handleAmountBlur}
        onKeyPress={handleKeyPress}
      />
      <Select
        disabled={disabled}
        options={MONEY_CURRENCY_CODES}
        value={localValue.currency}
        onChange={handleCurrencyChange}
      />
    </ProductFieldRow>
  );
};

MoneyField.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    currency: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  isDiff: PropTypes.bool,
  validate: PropTypes.func,
  savedError: PropTypes.string,
  onValidationChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

MoneyField.defaultProps = {
  value: '',
  disabled: false,
  isDiff: false,
  validate: null,
  savedError: null,
  onValidationChange: null,
};

export default MoneyField;
